import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import axios from "axios";
import Drawer from "@mui/material/Drawer";
import Alert from "@mui/material/Alert";
import APIHelper from "../utils/APIHelper";

export default function CustomersPage({ token }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    tags: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [editingId, setEditingId] = useState(null);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/customers`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { page, limit, search },
        }
      );
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.warn("Failed to load customers", e?.response?.data || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/customers/export`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.warn("Export failed", e);
    }
  };

  const fileInputRef = React.useRef(null);
  const handleImportClick = () => fileInputRef.current?.click();
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/customers/import`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        }
      );
      if (!res.ok) throw new Error("Import failed");
      await res.json();
      setPage(1);
      fetchCustomers();
    } catch (e) {
      console.warn("Import failed", e);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, limit]);

  const totalPages = useMemo(
    () => Math.max(Math.ceil(total / limit), 1),
    [total, limit]
  );

  return (
    <Box>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <PeopleIcon className="text-primary-600 dark:text-primary-400" />
          <Typography variant="h6" className="text-2xl font-bold text-gray-900 dark:text-white">
            Customers
          </Typography>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={handleImportClick}
            className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 normal-case"
          >
            Import CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 normal-case"
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingId(null);
              setForm({
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                tags: "",
                notes: "",
              });
              setErrorMessage("");
              setDrawerOpen(true);
            }}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
          >
            Add Customer
          </Button>
          <input
            type="file"
            accept=".csv"
            hidden
            ref={fileInputRef}
            onChange={handleImportFile}
          />
        </div>
      </div>

      <div className="card-modern p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <TextField
            size="small"
            placeholder="Search name, email, phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPage(1);
                fetchCustomers();
              }
            }}
            className="flex-1"
          />
          <Button
            onClick={() => {
              setPage(1);
              fetchCustomers();
            }}
            variant="outlined"
            className="border-primary-600 text-primary-600 hover:bg-primary-50 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20 normal-case"
          >
            Search
          </Button>
        </div>
      </div>

      <div className="card-modern p-4">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Name</TableCell>
                <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Email</TableCell>
                <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Phone</TableCell>
                <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Tags</TableCell>
                <TableCell align="right" className="text-gray-700 dark:text-gray-300 font-semibold">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((c) => {
                const fullName = [c.firstName, c.lastName]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <TableRow key={c._id} hover>
                    <TableCell className="text-gray-900 dark:text-white">{fullName || "—"}</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300">{c.email || "—"}</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300">{c.phone || "—"}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {(c.tags || []).slice(0, 3).map((t) => (
                          <Chip key={t} label={t} size="small" />
                        ))}
                        {(c.tags || []).length > 3 && (
                          <Chip
                            label={`+${(c.tags || []).length - 3}`}
                            size="small"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingId(c._id);
                          setForm({
                            firstName: c.firstName || "",
                            lastName: c.lastName || "",
                            email: c.email || "",
                            phone: c.phone || "",
                            tags: (c.tags || []).join("|"),
                            notes: c.notes || "",
                          });
                          setErrorMessage("");
                          setDrawerOpen(true);
                        }}
                        className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={async () => {
                          try {
                            if (!window.confirm("Delete this customer?")) return;
                            await APIHelper.deleteCustomer(token, c._id);
                            fetchCustomers();
                          } catch (e) {
                            console.warn(
                              "Delete failed",
                              e?.response?.data || e.message
                            );
                          }
                        }}
                        className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {items.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box py={4} textAlign="center" className="text-gray-500 dark:text-gray-400">
                      No customers yet.
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div className="flex items-center justify-end gap-2 mt-4">
        <Button
          size="small"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(p - 1, 1))}
          className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case"
        >
          Prev
        </Button>
        <Box px={1} display="flex" alignItems="center" className="text-gray-700 dark:text-gray-300">
          {page} / {totalPages}
        </Box>
        <Button
          size="small"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case"
        >
          Next
        </Button>
      </div>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 420 } },
          className: "bg-white dark:bg-gray-900"
        }}
      >
        <Box p={3} display="flex" flexDirection="column" gap={3} className="h-full">
          <Typography variant="h6" className="text-2xl font-bold text-gray-900 dark:text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <span className="text-xl">{editingId ? "✏️" : "➕"}</span>
              </div>
              <span>{editingId ? "Edit Customer" : "Add Customer"}</span>
            </div>
          </Typography>
          {errorMessage && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <div className="flex items-center justify-between">
                <Typography className="text-sm text-red-800 dark:text-red-200">
                  ⚠️ {errorMessage}
                </Typography>
                <button
                  onClick={() => setErrorMessage("")}
                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                >
                  &times;
                </button>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto space-y-3">
            <TextField
              label="First name"
              value={form.firstName}
              onChange={(e) =>
                setForm((f) => ({ ...f, firstName: e.target.value }))
              }
              size="small"
              fullWidth
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              label="Last name"
              value={form.lastName}
              onChange={(e) =>
                setForm((f) => ({ ...f, lastName: e.target.value }))
              }
              size="small"
              fullWidth
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              size="small"
              fullWidth
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              label="Phone"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              size="small"
              fullWidth
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              label="Tags (pipe separated)"
              placeholder="vip|lead"
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
              size="small"
              fullWidth
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              multiline
              minRows={3}
              fullWidth
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
          </div>
          <Stack direction="row" gap={2} justifyContent="flex-end" className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              onClick={() => setDrawerOpen(false)}
              className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case px-4"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={saving}
              onClick={async () => {
                try {
                  setSaving(true);
                  setErrorMessage("");
                  const payload = {
                    firstName: form.firstName.trim(),
                    lastName: form.lastName.trim(),
                    email: form.email.trim(),
                    phone: form.phone.trim(),
                    tags: form.tags
                      .split("|")
                      .map((t) => t.trim())
                      .filter(Boolean),
                    notes: form.notes,
                  };
                  // Basic validation: require at least one identifier
                  if (
                    !payload.firstName &&
                    !payload.lastName &&
                    !payload.email &&
                    !payload.phone
                  ) {
                    setErrorMessage("Enter at least a name, email, or phone.");
                    return;
                  }
                  if (
                    payload.email &&
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)
                  ) {
                    setErrorMessage("Please enter a valid email address.");
                    return;
                  }
                  if (editingId) {
                    await APIHelper.updateCustomer(token, editingId, payload);
                  } else {
                    await APIHelper.createCustomer(token, payload);
                  }
                  setDrawerOpen(false);
                  setForm({
                    firstName: "",
                    lastName: "",
                    email: "",
                    phone: "",
                    tags: "",
                    notes: "",
                  });
                  setEditingId(null);
                  setPage(1);
                  fetchCustomers();
                } catch (e) {
                  const apiMsg =
                    e?.response?.data?.message ||
                    e?.message ||
                    "Failed to save";
                  console.warn("Create failed", e?.response?.data || e.message);
                  setErrorMessage(apiMsg);
                } finally {
                  setSaving(false);
                }
              }}
              className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
            >
              {saving ? "Saving..." : editingId ? "Update" : "Save"}
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}
